(() => {
  if (customElements.get('hcx-cart-discount')) return;

  class HcxCartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hcxCartDiscountBound === 'true') return;

      this.form = this.querySelector('[data-hcx-cart-discount-form]');
      this.input = this.querySelector('input[name="discount"]');
      this.submitButton = this.querySelector('[data-hcx-cart-discount-submit]');
      this.submitLabel = this.querySelector('[data-hcx-cart-discount-submit-label]');
      this.status = this.querySelector('[data-hcx-cart-discount-status]');

      if (!this.form || !this.input || !this.submitButton || !this.status) return;

      this.dataset.hcxCartDiscountBound = 'true';
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.input.addEventListener('input', () => {
        this.input.removeAttribute('aria-invalid');
        if (this.status.dataset.state === 'error') this.clearStatus();
      });
    }

    normalizeCode(value) {
      return String(value || '').trim();
    }

    normalizeCodeKey(value) {
      return this.normalizeCode(value).toLocaleLowerCase();
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);
      this.submitButton.disabled = isBusy;
      this.input.disabled = isBusy;
      if (this.submitLabel) this.submitLabel.textContent = isBusy ? 'Applying…' : 'Apply';
    }

    setStatus(message, state) {
      this.status.textContent = message;
      this.status.dataset.state = state;
      this.status.hidden = false;
    }

    clearStatus() {
      this.status.textContent = '';
      delete this.status.dataset.state;
      this.status.hidden = true;
    }

    getCartUrl() {
      return window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    }

    getCartUpdateUrl() {
      return window.routes?.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
    }

    async getApplicableDiscountCodes() {
      const response = await fetch(`${this.getCartUrl()}.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

      const cart = await response.json();
      return Array.isArray(cart.discount_codes)
        ? cart.discount_codes
            .filter((discount) => discount?.code && discount.applicable !== false)
            .map((discount) => discount.code)
        : [];
    }

    getSectionsToRequest() {
      const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
      const cartItems = document.querySelector('cart-items');

      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        cartItems.getSectionsToRender().forEach((section) => {
          if (section?.section) sectionIds.push(section.section);
        });
      }

      return [...new Set(sectionIds)].slice(0, 5);
    }

    async updateDiscountCodes(codes, sections = []) {
      const payload = { discount: codes.join(',') };

      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(this.getCartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const cart = await response.json();
      if (!response.ok || cart.errors) {
        throw new Error(typeof cart.errors === 'string' ? cart.errors : `Cart update failed with status ${response.status}`);
      }

      return cart;
    }

    isCodeApplicable(cart, code) {
      const codeKey = this.normalizeCodeKey(code);
      const matchingCode = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.find((discount) => this.normalizeCodeKey(discount?.code) === codeKey)
        : null;

      if (matchingCode) return matchingCode.applicable !== false;

      const applications = [
        ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
        ...(Array.isArray(cart.items)
          ? cart.items.flatMap((item) => (Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : []))
          : []),
      ];

      return applications.some((entry) => {
        const application = entry?.discount_application || entry;
        return (
          application?.type === 'discount_code' && this.normalizeCodeKey(application.title || application.code) === codeKey
        );
      });
    }

    updateCartPageSections(cart) {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;

      cartItems.getSectionsToRender().forEach((section) => {
        const html = cart.sections?.[section.section];
        const container = document.getElementById(section.id);
        if (!html || !container) return;

        const elementToReplace = container.querySelector(section.selector) || container;
        const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
        const sourceElement = sourceDocument.querySelector(section.selector);
        if (sourceElement) elementToReplace.innerHTML = sourceElement.innerHTML;
      });
    }

    updateCartDrawerSilently(cart) {
      const cartDrawer = document.querySelector('cart-drawer');
      const html = cart.sections?.['cart-drawer'];
      if (!cartDrawer || !html) return;

      const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
      const sourceDrawer = sourceDocument.querySelector('cart-drawer');
      if (!sourceDrawer) return;

      ['.hcx-cart-progress', '.hcx-cart-summary-card', 'cart-drawer-items', '.cart-drawer__footer'].forEach(
        (selector) => {
          const targetElement = cartDrawer.querySelector(selector);
          const sourceElement = sourceDrawer.querySelector(selector);
          if (targetElement && sourceElement) targetElement.replaceWith(sourceElement.cloneNode(true));
        }
      );

      cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
    }

    updateCartIcon(cart) {
      const html = cart.sections?.['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (!html || !target) return;

      const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
      const source = sourceDocument.querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    }

    renderUpdatedCart(cart, submittedFromDrawer) {
      this.updateCartPageSections(cart);

      const cartDrawer = document.querySelector('cart-drawer');
      if (submittedFromDrawer && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(cart);
      } else {
        this.updateCartDrawerSilently(cart);
        this.updateCartIcon(cart);
      }
    }

    showResult(context, code, message, state) {
      window.setTimeout(() => {
        const scope =
          context === 'drawer' ? document.querySelector('cart-drawer') : document.getElementById('main-cart-footer');
        const nextForm = scope?.querySelector(
          `hcx-cart-discount[data-hcx-cart-discount-context="${CSS.escape(context)}"]`
        );
        if (!nextForm) return;

        nextForm.input.value = code;
        nextForm.input.toggleAttribute('aria-invalid', state === 'error');
        nextForm.setStatus(message, state);
        nextForm.input.focus({ preventScroll: true });
      }, context === 'drawer' ? 160 : 0);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.hasAttribute('aria-busy')) return;

      const code = this.normalizeCode(this.input.value);
      if (!code) {
        this.input.setAttribute('aria-invalid', 'true');
        this.setStatus('Enter a discount code.', 'error');
        this.input.focus();
        return;
      }

      const context = this.dataset.hcxCartDiscountContext || 'cart';
      const submittedFromDrawer = Boolean(this.closest('cart-drawer'));
      let existingCodes = [];

      this.input.removeAttribute('aria-invalid');
      this.clearStatus();
      this.setBusy(true);

      try {
        existingCodes = await this.getApplicableDiscountCodes();
        const codeKey = this.normalizeCodeKey(code);
        const codesToApply = [
          ...existingCodes.filter((existingCode) => this.normalizeCodeKey(existingCode) !== codeKey),
          code,
        ];
        const cart = await this.updateDiscountCodes(codesToApply, this.getSectionsToRequest());

        if (!this.isCodeApplicable(cart, code)) {
          await this.updateDiscountCodes(existingCodes).catch(() => null);
          this.input.setAttribute('aria-invalid', 'true');
          this.setStatus("This code couldn't be applied to this cart.", 'error');
          this.input.focus();
          return;
        }

        this.renderUpdatedCart(cart, submittedFromDrawer);
        this.showResult(context, code, 'Discount applied. Cart totals refreshed.', 'success');
      } catch (error) {
        console.error('Unable to apply cart discount.', error);
        this.input.setAttribute('aria-invalid', 'true');
        this.setStatus("We couldn't apply that code. Try again.", 'error');
        this.input.focus();
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }
  }

  customElements.define('hcx-cart-discount', HcxCartDiscount);
})();
